import React, { useState, memo } from 'react';
import { Box, IconButton, Tabs, Tab } from '@mui/material';
import { Fullscreen as FullscreenIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import DashboardPanel from './DashboardPanel';
import VideoPanel from './VideoPanel';
import InspectionPanel from './InspectionPanel';
import EventsPanel from './EventsPanel';
import EventSummaries from './EventSummaries';

const cornerButtonSx = {
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 10,
  bgcolor: 'background.paper',
  '&:hover': { bgcolor: 'background.paper', filter: 'brightness(0.92)' },
};

const EventsPanelWrapper = memo(({ appInfo, viewConfig }) => {
  const [showSummaries, setShowSummaries] = useState(true);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Tab bar */}
      <Tabs
        value={showSummaries ? 0 : 1}
        onChange={(_, val) => setShowSummaries(val === 0)}
        sx={{ flexShrink: 0, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Event Summaries" />
        <Tab label="Watch Log" />
      </Tabs>

      {/* Both views always mounted; inactive one hidden via display:none to preserve state */}
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, display: showSummaries ? 'flex' : 'none', flexDirection: 'column' }}>
        <EventSummaries appInfo={appInfo} />
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, display: showSummaries ? 'none' : 'flex', flexDirection: 'column' }}>
        <EventsPanel appInfo={appInfo} viewConfig={viewConfig} />
      </Box>
    </Box>
  );
});

const DashboardLayout = ({
  appInfo,
  runId = null,
  viewConfig = {},
  videoWidth,
  contentHeight,
  onVerticalMouseDown,
  onHorizontalMouseDown,
  fullScreenPanel = null,
  onFullScreenPanel
}) => {
  const inFullScreen = fullScreenPanel != null;
  const videoPanelActive = !!viewConfig?.videoSection;

  let gridAreas, gridCols, gridRows;

  if (inFullScreen) {
    gridAreas = `"${fullScreenPanel}"`;
    gridCols = '1fr';
    gridRows = '1fr';
  } else if (videoPanelActive) {
    gridAreas = `"video inspection" "events events"`;
    gridCols = `${videoWidth}% 1fr`;
    gridRows = `${contentHeight}% 1fr`;
  } else {
    gridAreas = `"inspection" "events"`;
    gridCols = '1fr';
    gridRows = `${contentHeight}% 1fr`;
  }

  const showHResize = !inFullScreen && videoPanelActive;
  const showVResize = !inFullScreen;

  const panelBoxSx = {
    position: 'relative',
    overflow: 'hidden',
    minHeight: 0,
    minWidth: 0,
  };

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateAreas: gridAreas,
      gridTemplateColumns: gridCols,
      gridTemplateRows: gridRows,
      gap: 1,
      p: 1,
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Video panel — only when video is configured or fullscreened */}
      {(videoPanelActive || fullScreenPanel === 'video') && (
        <Box sx={{ ...panelBoxSx, gridArea: 'video', display: inFullScreen && fullScreenPanel !== 'video' ? 'none' : undefined }}>
          <DashboardPanel>
            <VideoPanel appInfo={appInfo} runId={runId} viewConfig={viewConfig} fullScreen={fullScreenPanel === 'video'} />
          </DashboardPanel>
          {!inFullScreen && onFullScreenPanel && (
            <IconButton size="small" sx={cornerButtonSx} onClick={(e) => { e.stopPropagation(); onFullScreenPanel('video'); }} aria-label="Fullscreen video panel">
              <FullscreenIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* Inspection panel */}
      <Box sx={{ ...panelBoxSx, gridArea: 'inspection', display: inFullScreen && fullScreenPanel !== 'inspection' ? 'none' : undefined }}>
        <DashboardPanel>
          <InspectionPanel appInfo={appInfo} runId={runId} viewConfig={viewConfig} />
        </DashboardPanel>
        {!inFullScreen && onFullScreenPanel && (
          <IconButton size="small" sx={cornerButtonSx} onClick={(e) => { e.stopPropagation(); onFullScreenPanel('inspection'); }} aria-label="Fullscreen inspection panel">
            <FullscreenIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Events panel */}
      <Box sx={{ ...panelBoxSx, gridArea: 'events', display: inFullScreen && fullScreenPanel !== 'events' ? 'none' : undefined }}>
        <DashboardPanel sx={{ p: 0 }}>
          <EventsPanelWrapper appInfo={appInfo} viewConfig={viewConfig} />
        </DashboardPanel>
        {!inFullScreen && onFullScreenPanel && (
          <IconButton size="small" sx={cornerButtonSx} onClick={(e) => { e.stopPropagation(); onFullScreenPanel('events'); }} aria-label="Fullscreen events panel">
            <FullscreenIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Resize handles */}
      {showHResize && (
        <Box
          onMouseDown={onHorizontalMouseDown}
          sx={{
            position: 'absolute',
            left: `calc(${videoWidth}% + 4px)`,
            top: 8,
            bottom: showVResize ? `calc(${100 - contentHeight}% + 4px)` : 8,
            width: 8,
            cursor: 'col-resize',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { '& .resize-indicator': { bgcolor: 'primary.main' } },
          }}
        >
          <Box className="resize-indicator" sx={{ width: 4, height: '100%', bgcolor: 'divider', borderRadius: 1, transition: 'background-color 0.15s' }}>
            <DragIndicatorIcon sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 16, color: 'text.secondary' }} />
          </Box>
        </Box>
      )}

      {showVResize && (
        <Box
          onMouseDown={onVerticalMouseDown}
          sx={{
            position: 'absolute',
            top: `calc(${contentHeight}% + 4px)`,
            left: 8,
            right: 8,
            height: 8,
            cursor: 'row-resize',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { '& .resize-indicator': { bgcolor: 'primary.main' } },
          }}
        >
          <Box className="resize-indicator" sx={{ height: 4, width: '100%', bgcolor: 'divider', borderRadius: 1, transition: 'background-color 0.15s' }}>
            <DragIndicatorIcon sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(90deg)', fontSize: 16, color: 'text.secondary' }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DashboardLayout;
